// Subscription tier access registry — single source of truth for which
// route requires which minimum tier. Keep this file alphabetized by
// route path within each tier for easy scanning.
//
// To move a tool between tiers: find its entry and change the required
// tier. Nothing else needs to change — TierGate and the Sidebar both
// read from this registry.

export type SubscriptionTier = "free" | "select" | "choice";

// Numeric rank for tier comparisons. Higher = more access.
export const TIER_RANK: Record<SubscriptionTier, number> = {
  free:   0,
  select: 1,
  choice: 2,
};

/**
 * Routes that require a minimum subscription tier. Keys are route paths
 * (pattern-matched — see `tierForPath`). Any route NOT listed here
 * defaults to `select` (the entry paid tier).
 *
 * Order within each tier doesn't matter; grouping is for human
 * readability.
 */
export const ROUTE_TIER: Array<{ pattern: string; tier: SubscriptionTier }> = [
  // ─── Always public (no auth required) ──────────────────────────────────
  // These don't go through TierGate at all — they're routed outside the
  // protected Layout wrapper in App.tsx. Listed here for documentation
  // only.
  // { pattern: "/auth", tier: "free" },
  // { pattern: "/features", tier: "free" },
  // { pattern: "/terms", tier: "free" },
  // { pattern: "/privacy", tier: "free" },

  // ─── Staff tooling — gated on ROLE, never on tier ───────────────────────
  //
  // ⚠️ Admin pages are for employees, not customers, so a SUBSCRIPTION must
  // never decide who can reach them. Without this line the fallback at the
  // bottom of `tierForPath` applies and every /admin/* route resolves to
  // `select`, which means an admin whose tier is `free` gets sent to
  // /upgrade and can never administer anything.
  //
  // That was mostly invisible because `useSubscriptionTier` defaults a user
  // with no `profiles` row to "select" — the same masking that neutered
  // BarnOnlyLock (see its header, and CLAUDE.md's rule: gate on role, never
  // on tier). It became worth fixing when /manage/tools arrived, because that
  // page curates raisedbeef.com: leaving it here would have made managing the
  // FREE public site depend on holding a paid Terminal subscription, coupling
  // two products that are deliberately separate.
  //
  // Safe because this is not the security boundary. Every admin page checks
  // `useIsAdmin()` itself, and every admin RPC re-checks
  // `has_role(auth.uid(),'admin')` server-side — which is what actually keeps
  // non-admins out.
  { pattern: "/admin/*",                             tier: "free" },
  // ⚠️ AND /manage/*, WHICH THE NOTE ABOVE WAS WRITTEN FOR AND NEVER COVERED.
  // The paragraph says this entry "became worth fixing when /manage/tools
  // arrived" — and then only `/admin/*` was listed, so `/manage/tools` fell
  // through to the `select` fallback at the bottom of tierForPath. It was
  // invisible because that route is mounted OUTSIDE Layout and TierGate
  // (App.tsx), so nothing consults this registry for it today. The day anyone
  // moves it inside — or adds a second /manage page that is inside — an admin
  // on the `free` tier is sent to /upgrade and cannot administer the free
  // public site, which is precisely the coupling the note exists to prevent.
  // Found 2026-09-13 while correcting the /admin/tools path, which is not and
  // never was a route.
  { pattern: "/manage/*",                            tier: "free" },

  // ─── Select tier — basic paid access ────────────────────────────────────
  // The intentional "starter set" a Select subscriber gets.
  // Note: `/` itself is the free public landing page (ClaraLiveLanding);
  // the authenticated live market dashboard lives at `/live`.
  { pattern: "/live",                                tier: "select" },
  { pattern: "/dashboard",                           tier: "select" },
  { pattern: "/calf-tracker",                        tier: "select" },
  { pattern: "/tools/feeders/national-steer-tracker", tier: "select" },
  { pattern: "/tools/feeders/steer-tracker-daily",    tier: "choice" },
  { pattern: "/tools/feeders/clara-live-tracker",     tier: "select" },
  { pattern: "/tools/feeders/state-direct",          tier: "choice" },
  { pattern: "/tools/drought",                        tier: "select" },
  { pattern: "/tools/test-an-assumption",             tier: "choice" },
  { pattern: "/tools/drought-pressure",               tier: "select" },
  { pattern: "/market-calendar",                      tier: "select" },
  { pattern: "/pasture-stress",                       tier: "select" },
  { pattern: "/quick-view/barn/:name",               tier: "select" },
  { pattern: "/quick-view/state/:state",             tier: "select" },
  { pattern: "/quick-view/region/:region",           tier: "select" },
  { pattern: "/settings",                            tier: "select" },
  { pattern: "/upgrade",                             tier: "select" },

  // Daily briefs — all 4 brief types accessible at Select tier. Subscription
  // (user_brief_subscriptions) controls email delivery + Dashboard surfacing
  // independently of read access. A Select subscriber can still read all
  // briefs even if they only subscribe to one for email.
  { pattern: "/briefs",                              tier: "select" },
  { pattern: "/briefs/stocker",                      tier: "select" },
  { pattern: "/briefs/saleday",                      tier: "select" },
  { pattern: "/briefs/feedyard",                     tier: "select" },

  // Hub nav containers. They auto-hide when no child tool matches the
  // user's tier — the Sidebar does that filtering. Listed here with
  // `select` so a Select user CAN navigate to hubs that contain Select
  // tools (e.g., /feeders contains National Steer Tracker).
  { pattern: "/calves",                              tier: "select" },
  { pattern: "/feeders",                             tier: "select" },

  // Onboarding must be reachable by any logged-in user
  { pattern: "/onboarding/operator-type",            tier: "select" },

  // ─── Choice tier — premium paid access ──────────────────────────────────
  // Everything else in the protected portion of the app.

  // Hub pages with no Select children
  { pattern: "/tools/choice-daily-cutout",           tier: "choice" },
  { pattern: "/tools/usda-fed-cattle-forecast",      tier: "choice" },
  { pattern: "/tools/usda-forecast-path",            tier: "choice" },
  { pattern: "/tools/usda-track-record",             tier: "choice" },
  { pattern: "/tools/usda-corn-outlook",             tier: "choice" },
  { pattern: "/tools/wasde-this-month",              tier: "choice" },
  { pattern: "/tools/usda-beef-supply",              tier: "choice" },
  { pattern: "/fed-cattle",                          tier: "choice" },
  { pattern: "/fed-cattle-auction",                  tier: "choice" },
  { pattern: "/fat-cattle-sales",                    tier: "choice" },
  { pattern: "/barn-vs-packer",                      tier: "choice" },
  { pattern: "/beef-supply",                         tier: "choice" },
  { pattern: "/cow-calf",                            tier: "choice" },
  { pattern: "/cow-calf/economics",                  tier: "choice" },
  { pattern: "/my-operation",                        tier: "choice" },
  { pattern: "/insights",                            tier: "choice" },

  // Clara Live Cattle Consultant — full-page chat surface
  { pattern: "/consultant",                          tier: "choice" },

  // Market intelligence tools
  { pattern: "/market-report",                       tier: "choice" },
  { pattern: "/market-pulse",                        tier: "choice" },
  { pattern: "/tools/per-head-supply",                tier: "choice" },
  { pattern: "/tools/cold-storage",                   tier: "choice" },
  { pattern: "/tools/cot-positioning",                tier: "choice" },
  { pattern: "/tools/feeding-extension",              tier: "choice" },
  { pattern: "/tools/value-added-premium",            tier: "choice" },
  { pattern: "/tools/barn-price-ranking",             tier: "choice" },
  { pattern: "/tools/value-of-gain",                  tier: "choice" },
  { pattern: "/tools/video-forward-market",           tier: "choice" },
  { pattern: "/regional-markets",                    tier: "choice" },
  { pattern: "/supply-demand",                       tier: "choice" },
  { pattern: "/cattle-on-feed",                      tier: "choice" },
  { pattern: "/tools/cattle-inventory",               tier: "choice" },
  { pattern: "/dv-auction",                          tier: "choice" },
  { pattern: "/freight",                             tier: "choice" },
  { pattern: "/profitability",                       tier: "choice" },
  { pattern: "/report-builder",                      tier: "choice" },

  // Packer tools
  { pattern: "/tools/packer-posture",                tier: "choice" },
  { pattern: "/tools/primal-seasonality",            tier: "choice" },
  { pattern: "/tools/holiday-cut-tracker",           tier: "choice" },
  { pattern: "/tools/steak-burger-gauge",            tier: "choice" },
  { pattern: "/tools/packer-leverage",               tier: "choice" },

  // Fed cattle tools
  { pattern: "/tools/fed-cattle/recent-trades",      tier: "choice" },
  { pattern: "/tools/fed-cattle/day-of-week",        tier: "choice" },
  { pattern: "/tools/fed-cattle/seasonal-setup",     tier: "choice" },
  { pattern: "/tools/fed-cattle/regional-pulse",     tier: "choice" },
  { pattern: "/tools/fed-cattle/choice-select",      tier: "choice" },
  { pattern: "/tools/fed-cattle/leverage-index",     tier: "choice" },
  { pattern: "/tools/fed-cattle/captive-supply",     tier: "choice" },
  { pattern: "/tools/fed-cattle/cash-vs-grid-spread", tier: "choice" },
  { pattern: "/tools/fed-cattle/price-distribution", tier: "choice" },
  { pattern: "/tools/fed-cattle/cash-seasonality",   tier: "choice" },

  // News + research
  // /news/briefs renders the General brief — downgraded to select alongside
  // the other 3 brief routes so paid users can read any brief.
  { pattern: "/news/briefs",                         tier: "select" },
  { pattern: "/news/weekly",                         tier: "choice" },

  // Data / admin-adjacent
  { pattern: "/data-integrations",                   tier: "choice" },
  { pattern: "/data-integrations/schema",            tier: "choice" },
  { pattern: "/data-integrations/architecture",      tier: "choice" },
  { pattern: "/email-dashboard",                     tier: "choice" },

  // Detail pages
  { pattern: "/reports/premium/:slug",               tier: "choice" },
  { pattern: "/market/:category/:state/:weightClass", tier: "choice" },
  { pattern: "/cohort/:region/:weight",              tier: "choice" },
  { pattern: "/barn/:slug",                          tier: "choice" },
];

/**
 * Returns whether a user with `userTier` can access a route requiring
 * `requiredTier`. Higher tiers can always access lower-tier content.
 */
export function canAccessTier(
  userTier: SubscriptionTier,
  requiredTier: SubscriptionTier,
): boolean {
  return TIER_RANK[userTier] >= TIER_RANK[requiredTier];
}

/**
 * Resolve the required tier for a given pathname. Matches exact paths
 * first, then pattern routes (`:param` segments). Returns `select` as
 * the default for any route not explicitly listed — that's the minimum
 * paid tier.
 */
export function tierForPath(pathname: string): SubscriptionTier {
  // Exact match first
  const exact = ROUTE_TIER.find((r) => r.pattern === pathname);
  if (exact) return exact.tier;

  // Then pattern match — convert `:param` to a regex wildcard
  for (const row of ROUTE_TIER) {
    if (!row.pattern.includes(":")) continue;
    const regex = new RegExp(
      "^" + row.pattern.replace(/:[^/]+/g, "[^/]+") + "$",
    );
    if (regex.test(pathname)) return row.tier;
  }

  // Then explicit PREFIX rules, written with a trailing `/*`.
  //
  // Opt-in rather than implicit: a bare "/admin" entry matching every
  // "/admin/..." path would mean that later adding, say, "/tools" as an exact
  // rule silently re-tiers every unlisted "/tools/..." route too. The `/*` has
  // to be typed, so the intent is visible in the table.
  //
  // Runs AFTER exact and `:param` matching, so a specific rule always wins
  // over a prefix — and it can only change the answer for routes that would
  // otherwise fall through to the default below.
  for (const row of ROUTE_TIER) {
    if (!row.pattern.endsWith("/*")) continue;
    const base = row.pattern.slice(0, -2);
    if (pathname === base || pathname.startsWith(base + "/")) return row.tier;
  }

  // Unknown protected route → default to Select (the entry paid tier)
  return "select";
}
